import React, { useState, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { AddMailButtonWrapper, AddMailButtonWrapperMobile, AddMailButtonWrappedRight, MailsWrapper } from './styles'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { UserMailTypeCategory, UserMailsResult, UserMailsVariables } from 'graphql/mails/queries'
import { displayErrors } from 'globals/graphql'
import Mail from 'globals/models/Mail'
import { Domiciliation } from 'globals/models/Domiciliation'
import popupSuccess from 'globals/popupSuccess'
import { Button, Paginator, SearchBar, SelectedMailsButtons } from 'components'
import { AddIcon } from 'components/Icons'
import AddMail from 'pages/AddMail'
import ClientMails from 'pages/ClientDetails/components/ClientMails'
import ClientMailsMobile from 'pages/ClientDetails/components/ClientMailsMobile'
import MailDetails from 'pages/ClientDetails/components/MailDetails'
import {
  MARK_MAILS_AS_DISPOSED,
  MarkMailsAsDisposedResult,
  MarkMailsAsDisposedVariables
} from 'pages/ClientDetails/components/ClientMailsTab/mutations'
import { Select } from 'antd'

interface IProps {
  domiciliation: Domiciliation
  fetchMails: ({ variables }: { variables: UserMailsVariables }) => void
  loading: boolean
  data: UserMailsResult
  refetch: () => void
}

const ClientMailsTab = (props: IProps) => {
  const { domiciliation, fetchMails, loading, data, refetch } = props
  const { t } = useTranslation()
  const pageSize = 10

  const [mailDetails, setMailDetails] = useState<Mail | null>(null)
  const [showMailDetails, setShowMailDetails] = useState(false)
  const [showAddMail, setShowAddMail] = useState(false)
  const [isFromUpdateMail, setIsFromUpdateMail] = useState<boolean | undefined>(undefined)
  const [selectedMailsIds, setSelectedMailsIds] = useState<React.Key[]>([])
  const [searchParams, setSearchParams] = useState({
    searchTerm: '',
    currentPage: 1,
    mailTypeCategory: UserMailTypeCategory.All
  })

  const [markAsDisposed] = useMutation<MarkMailsAsDisposedResult, MarkMailsAsDisposedVariables>(
    MARK_MAILS_AS_DISPOSED,
    displayErrors
  )

  const onMarkAsDisposed = () =>
    markAsDisposed({ variables: { userMailIds: selectedMailsIds.map(Number) } }).then(() => {
      popupSuccess('Courrier(s) marqué(s) comme remis')
      refetch!()
      setSelectedMailsIds([])
    })

  const onMailClicked = (mail: Mail) => {
    setMailDetails(mail)
    setShowMailDetails(true)
  }

  const setShowAddMailAndResetForm = (doit: boolean) => {
    setMailDetails(null)
    setShowAddMail(doit)
  }

  const searchMails = () => {
    fetchMails({
      variables: {
        mailTypeCategory: searchParams.mailTypeCategory,
        search: searchParams.searchTerm,
        domiciliationId: domiciliation.id,
        page: searchParams.currentPage,
        pageLength: pageSize
      },
      ...displayErrors
    })
  }

  useEffect(searchMails, [searchParams])
  useEffect(searchMails, [])

  return (
    <MailsWrapper>
      <>
        <AddMailButtonWrapper>
          {loading ? (
            <div />
          ) : selectedMailsIds.length == 0 ? (
            <Button disabled={domiciliation.status != 'activated'} onClick={() => setShowAddMailAndResetForm(true)}>
              {t('clients.add-mail')}
            </Button>
          ) : (
            <SelectedMailsButtons
              selectedMailsNumber={selectedMailsIds.length}
              confirmText={t('mails.mark-delivered')}
              onConfirm={onMarkAsDisposed}
              onCancel={() => setSelectedMailsIds([])}
            />
          )}

          <AddMailButtonWrappedRight>
            <SearchBar
              onSearch={() => null}
              onChange={(value: string) =>
                setSearchParams((prev) => {
                  prev.searchTerm = value
                  prev.currentPage = 1
                  return { ...prev }
                })
              }
              value={searchParams.searchTerm}
              hideFilters
              placeholder={'Rechercher ID'}
              marginBottom={false}
              height={'31px'}
              backgroundColor={'#F0F1F3'}
            />

            <Select
              style={{ width: 180, marginRight: 12 }}
              value={searchParams.mailTypeCategory}
              onChange={(value: string) =>
                setSearchParams((prev) => {
                  prev.mailTypeCategory = value as UserMailTypeCategory
                  prev.currentPage = 1
                  return { ...prev }
                })
              }
              options={[
                { value: UserMailTypeCategory.All, label: 'Tous les courriers' },
                { value: UserMailTypeCategory.Colis, label: 'Colis' },
                { value: UserMailTypeCategory.Courrier, label: 'Courrier' }
              ]}
            />

            <Paginator
              currentPage={data?.userMails?.pagination?.currentPage || 1}
              totalPages={data?.userMails?.pagination?.totalPages || 1}
              onChangePage={(page: number) => setSearchParams({ ...searchParams, currentPage: page })}
              disabled={loading}
            />
          </AddMailButtonWrappedRight>
        </AddMailButtonWrapper>

        <AddMailButtonWrapperMobile>
          <span>{t('global.add')}</span>

          <Button onClick={() => setShowAddMailAndResetForm(true)}>
            <AddIcon />
          </Button>
        </AddMailButtonWrapperMobile>

        {data?.userMails?.mails &&
          (isMobile ? (
            <ClientMailsMobile
              mails={data.userMails.mails}
              selectedMailsIds={selectedMailsIds}
              setSelectedMailsIds={setSelectedMailsIds}
              onMailClicked={onMailClicked}
            />
          ) : (
            <ClientMails
              data={data.userMails.mails}
              selectedMailsIds={selectedMailsIds}
              setSelectedMailsIds={setSelectedMailsIds}
              onMailClicked={onMailClicked}
              currentPage={searchParams.currentPage}
              pageSize={pageSize}
              alwaysShowCheckboxes={false}
              lineStyle={'client'}
            />
          ))}
      </>

      <MailDetails
        domiciliation={domiciliation}
        mail={mailDetails}
        visible={showMailDetails}
        onClose={() => setShowMailDetails(false)}
        setShowAddMail={setShowAddMail}
        setIsFromUpdateMail={setIsFromUpdateMail}
        refetch={refetch}
      />

      <AddMail
        domiciliation={domiciliation}
        visible={showAddMail}
        onClose={() => setShowAddMail(false)}
        selectedMail={mailDetails}
        isFromUpdateMail={isFromUpdateMail}
        setIsFromUpdateMail={setIsFromUpdateMail}
        refetch={refetch}
      />
    </MailsWrapper>
  )
}

export default ClientMailsTab
